import { z } from 'zod';

import { countryCodeSchema } from '@apple/utils/globalization/models';
import type { Guid } from '@apple/utils/primitive';

import { type AssociationType, associationTypeSchema } from '@apple/features/associations/models';
import type { ProductKey } from '@apple/features/product/models/product';
import { productKeySchema } from '@apple/features/product/models/product';
import type {
	CountryStoreEventRule,
	GlobalStoreEventRule,
	MarketStoreEventRule,
	PlantStoreEventRule,
	StoreEventProductRule,
} from '@apple/features/product-retail/models/store-events';
import {
	countryStoreEventRuleSchema,
	globalStoreEventRuleSchema,
	marketStoreEventRuleSchema,
	plantStoreEventRuleSchema,
} from '@apple/features/product-retail/models/store-events';
import type { StoreId } from '@apple/features/store/models';

import { type StoreEventType, storeEventTypeSchema } from './shared';

/**
 * @see Manzanita.DataModels.ActionType
 */
export type Action = z.infer<typeof actionSchema>;

/**
 * @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventProductAssociationModel
 */
interface StoreEventProductAssociation<
	T extends AssociationType,
	K extends null | string | number,
> {
	categoryId: Guid;
	eventType: StoreEventType;
	quantity: number;
	accessType: T;
	accessConfiguration: K;
}

interface AllAssociation extends StoreEventProductAssociation<'All', null> {}
interface CountryAssociation extends StoreEventProductAssociation<'Country', string> {}
interface PlantAssociation extends StoreEventProductAssociation<'Plant', string> {}
interface MarketAssociation extends StoreEventProductAssociation<'CustomerGroup', string> {}

type Association = AllAssociation | CountryAssociation | PlantAssociation | MarketAssociation;

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationActionModel
 */
export type PropagationActionRequest = {
	itemId: ProductKey;
	action: Action;
	association: Association;
};

interface BasePropagation<T> {
	propagationAction: T;
	storeEventId: Guid;
	storeEventItemId: Guid | null;
	quantityMultiplier: number;
}

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationModel
 * Used to propagate the item rule changes to the store event
 */
export interface PropagationRequest extends BasePropagation<PropagationActionRequest> {}

interface BaseCandidate {
	storeName: string;
	thirdPartyStoreId: StoreId;
	quantity: number | null;
	isLocked: boolean;
	isApplicable: boolean;
}

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationCandidateModel
 */
export interface PropagationCandidateModel
	extends BasePropagation<PropagationActionRequest>,
		BaseCandidate {}

/**
 * @see Manzanita.DataModels.ActionType
 */
export const actionSchema = z.enum(['Add', 'Update', 'Remove']);

/**
 * @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventProductAssociationModel
 */
const storeEventAssociationSchema = z.object({
	categoryId: z.string(),
	eventType: storeEventTypeSchema,
	quantity: z.number(),
	accessType: associationTypeSchema,
});

const allAssociationSchema = storeEventAssociationSchema.extend({
	accessType: z.literal('All'),
	accessConfiguration: z.null(),
});
const countryAssociationSchema = storeEventAssociationSchema.extend({
	accessType: z.literal('Country'),
	accessConfiguration: countryCodeSchema,
});
const plantAssociationSchema = storeEventAssociationSchema.extend({
	accessType: z.literal('Plant'),
	accessConfiguration: z.coerce.string(),
});
const marketAssociationSchema = storeEventAssociationSchema.extend({
	accessType: z.literal('CustomerGroup'),
	accessConfiguration: z.coerce.string(),
});

const associationSchema = z.union([
	allAssociationSchema,
	countryAssociationSchema,
	plantAssociationSchema,
	marketAssociationSchema,
]);

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationActionModel
 */
export const propagationActionRequestSchema = z.object({
	itemId: productKeySchema,
	action: actionSchema,
	association: associationSchema,
}) satisfies z.ZodType<PropagationActionRequest>;

function createPropagationSchema<T extends z.ZodType>(actionSchema: T) {
	return z.object({
		propagationAction: actionSchema,
		storeEventId: z.string(),
		storeEventItemId: z.string().nullable(),
		quantityMultiplier: z.number(),
	});
}

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationModel
 * Used to propagate the item rule changes to the store event
 */
export const propagationRequestSchema = createPropagationSchema(
	propagationActionRequestSchema,
) satisfies z.ZodType<PropagationRequest>;

const baseCandidateSchema = z.object({
	storeName: z.string(),
	thirdPartyStoreId: z.string(),
	quantity: z.number().nullable(),
	isLocked: z.boolean(),
	isApplicable: z.boolean(),
});

/**
 * @see AppleRetail.Web.Features.StoreEvents.StoreEventProductChangePropagationController.PropagationCandidateModel
 */
export const propagationCandidateModelSchema = createPropagationSchema(
	propagationActionRequestSchema,
).and(baseCandidateSchema) satisfies z.ZodType<PropagationCandidateModel>;

/**
 * The models below represent store event rules based on the collections in the product entity's
 * store event configuration property.
 */
type RuleWithAssociationType =
	| {
			associationType: 'global';
			association: GlobalStoreEventRule;
	  }
	| {
			associationType: 'plant';
			association: PlantStoreEventRule;
	  }
	| {
			associationType: 'country';
			association: CountryStoreEventRule;
	  }
	| {
			associationType: 'market';
			association: MarketStoreEventRule;
	  };

export type PropagationAction = { itemId: ProductKey; action: Action } & RuleWithAssociationType;

export interface Propagation extends BasePropagation<PropagationAction> {}

export interface PropagationCandidate extends Propagation, BaseCandidate {}

const ruleWithAssociationTypeSchema = z.union([
	z.object({
		associationType: z.literal('global'),
		association: globalStoreEventRuleSchema,
	}),
	z.object({
		associationType: z.literal('plant'),
		association: plantStoreEventRuleSchema,
	}),
	z.object({
		associationType: z.literal('country'),
		association: countryStoreEventRuleSchema,
	}),
	z.object({
		associationType: z.literal('market'),
		association: marketStoreEventRuleSchema,
	}),
]);

export const propagationActionSchema = z
	.object({
		itemId: productKeySchema,
		action: actionSchema,
	})
	.and(ruleWithAssociationTypeSchema) satisfies z.ZodType<PropagationAction>;

export const propagationSchema = createPropagationSchema(
	propagationActionSchema,
) satisfies z.ZodType<Propagation>;
export const propagationCandidateSchema = createPropagationSchema(propagationActionSchema).and(
	baseCandidateSchema,
) satisfies z.ZodType<PropagationCandidate>;

// Converts a PropagationCandidateModel to a PropagationCandidate - the associations are transformed
// to store event product rules e.g. types in the store event configuration property on the product.
export const requestCandidatesToProductRuleSchema =
	propagationCandidateModelSchema.transform<PropagationCandidate>(x => {
		return {
			...x,
			propagationAction: {
				...x.propagationAction,
				...associationToRuleWithTypeSchema(x.propagationAction.itemId).parse(
					x.propagationAction.association,
				),
			},
		};
	});

export const propagationActionToPropagationActionRequestSchema =
	propagationActionSchema.transform<PropagationActionRequest>(x => {
		const base = {
			categoryId: x.association.storeEventCategoryId,
			eventType: x.association.eventType as StoreEventType,
			quantity: x.association.defaultQuantity,
		};

		const association: Association = (() => {
			switch (x.associationType) {
				case 'global':
					return allAssociationSchema.parse({
						...base,
						accessType: 'All',
						accessConfiguration: null,
					});
				case 'plant':
					return plantAssociationSchema.parse({
						...base,
						accessType: 'Plant',
						accessConfiguration: x.association.warehouseLocationId,
					});
				case 'country':
					return countryAssociationSchema.parse({
						...base,
						accessType: 'Country',
						accessConfiguration: x.association.countryCode,
					});
				case 'market':
					return marketAssociationSchema.parse({
						...base,
						accessType: 'CustomerGroup',
						accessConfiguration: x.association.customerGroupId,
					});
			}
		})();

		return {
			action: x.action,
			itemId: x.itemId,
			association,
		};
	});

export const propagationToPropagationRequestSchema =
	propagationSchema.transform<PropagationRequest>(x => {
		return {
			propagationAction: propagationActionToPropagationActionRequestSchema.parse(
				x.propagationAction,
			),
			storeEventId: x.storeEventId,
			storeEventItemId: x.storeEventItemId,
			quantityMultiplier: x.quantityMultiplier,
		};
	});

const numericAccessConfigSchema = z.coerce.number();

const associationToRuleWithTypeSchema = (itemId: ProductKey) =>
	associationSchema.transform<RuleWithAssociationType>(x => {
		const rule = {
			itemId: itemId,
			storeEventCategoryId: x.categoryId,
			eventType: x.eventType,
			defaultQuantity: x.quantity,
		} satisfies StoreEventProductRule;

		switch (x.accessType) {
			case 'All':
				return {
					associationType: 'global',
					association: rule,
				};
			case 'Plant':
				return {
					associationType: 'plant',
					association: {
						...rule,
						warehouseLocationId: numericAccessConfigSchema.parse(x.accessConfiguration),
					},
				};
			case 'Country':
				return {
					associationType: 'country',
					association: {
						...rule,
						countryCode: x.accessConfiguration,
					},
				};
			case 'CustomerGroup':
				return {
					associationType: 'market',
					association: {
						...rule,
						customerGroupId: numericAccessConfigSchema.parse(x.accessConfiguration),
					},
				};
		}
	});
